/**
 * reconcileOrderFee — RPC contract for the operator's on-demand fee re-check,
 * the third and last mechanism that may write `Order.fees`/`Order.feeStatus`
 * after birth. The other two are the settle webhook's birth stamp and the
 * hourly `feePoll` sweep; all three enter one core, so the operator's door and
 * the tick can never resolve the same order differently.
 *
 * The door exists because settlement lag is unbounded from the operator's
 * side: an order can sit `pending` for hours with the next tick up to sixty
 * minutes away, and the console's fee line is what a human is looking at when
 * they want to know now. It asks the provider for exactly one order and
 * writes what comes back.
 *
 * The call names the order and the event that owns it, never the workspace:
 *
 * Fields justified:
 *   eventId — the order's parent event; the path is
 *             workspaces/{orgId}/events/{eventId}/orders/{orderId} and the
 *             order id alone does not locate the document.
 *   orderId — the order whose fee is being re-checked.
 * Excluded:
 *   a force flag — a settled report is final and no caller may re-open it
 *           (order.entity.ts's `fees` describe), so there is nothing for a
 *           flag to override.
 *
 * Output is the outcome alone. The fee entries themselves are NOT carried
 * back: they land on the order document, which every caller of this door is
 * already reading live, and a copy on the wire is a second representation of
 * one fact free to disagree with the first.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

export const ReconcileOrderFeeInput = z
	.object({
		orgId: Id,
		eventId: Id.describe("the event the order being re-checked belongs to"),
		orderId: Id.describe("the order whose provider fee report is re-checked"),
	})
	.strict();
export type ReconcileOrderFeeInput = z.infer<typeof ReconcileOrderFeeInput>;

/**
 * What one order's reconcile resolved to — the single source for this
 * vocabulary, read by the callable's own output and by the sweep that shares
 * its core. Three tokens are the resting `Order.feeStatus` the reconcile left
 * behind; `noPspLeg` is the fourth answer that state cannot carry, because an
 * order with no payment collapses into `reported` on the document while
 * meaning something different entirely.
 */
export const FeeOutcomeSchema = z
	.enum(["reported", "pending", "unavailable", "noPspLeg"])
	.describe(
		"how the order's fee stands after the reconcile — reported: the provider's entries are on the order and the report is final; pending: the provider has not settled the fee yet and the hourly sweep keeps asking; unavailable: nothing was ever reported and the wait is over, so no figure is coming; noPspLeg: the order carries no payment, so there is no provider to ask",
	);
export type FeeOutcome = z.infer<typeof FeeOutcomeSchema>;

export const ReconcileOrderFeeOutput = z
	.object({ outcome: FeeOutcomeSchema })
	.strict();
export type ReconcileOrderFeeOutput = z.infer<typeof ReconcileOrderFeeOutput>;
